import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
  Upload, Link2, Trash2, Loader2, Percent, Search, BookMarked, Save, FileSpreadsheet, Plus, Hammer,
} from "lucide-react";
import api, { cachedGet } from "@/lib/api";
import { euro, formatApiErrorDetail } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";

export default function Preziario() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [books, setBooks] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [items, setItems] = useState([]);
  const [markup, setMarkup] = useState(15);
  const [mano, setMano] = useState("");
  const [savingMano, setSavingMano] = useState(false);
  const [q, setQ] = useState("");
  const [importing, setImporting] = useState(false);
  const [savingMarkup, setSavingMarkup] = useState(false);
  const [url, setUrl] = useState("");
  const [replaceMode, setReplaceMode] = useState(false);
  const fileRef = useRef(null);
  const [newOpen, setNewOpen] = useState(false);
  const [nb, setNb] = useState({ nome: "", tipo: "personale", markup: 15 });

  const loadBooks = useCallback(async () => {
    const r = await cachedGet("/preziari");
    setBooks(r.data);
    setSelectedId((cur) => cur || (r.data[0]?.id ?? null));
  }, []);
  useEffect(() => { loadBooks(); }, [loadBooks]);

  const loadItems = useCallback((id) => {
    if (!id) { setItems([]); return; }
    cachedGet(`/preziari/${id}/items`).then((r) => {
      setItems(r.data.items || []);
      setMarkup(r.data.markup ?? 15);
      const m = (r.data.items || []).find((i) => i.codice === "MANO");
      setMano(m ? m.prezzo_base : "");
    }).catch(() => {});
  }, []);
  useEffect(() => { loadItems(selectedId); }, [selectedId, loadItems]);

  const selected = books.find((b) => b.id === selectedId);

  const refresh = async () => { await loadBooks(); loadItems(selectedId); };

  const createBook = async () => {
    if (!nb.nome.trim()) { toast.error("Inserisci un nome per il preziario"); return; }
    try {
      const r = await api.post("/preziari", { nome: nb.nome.trim(), tipo: nb.tipo, markup: Number(nb.markup) || 0 });
      toast.success("Preziario creato");
      setNewOpen(false);
      await loadBooks();
      setSelectedId(r.data.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveMarkup = async () => {
    if (!selected) return;
    setSavingMarkup(true);
    try {
      await api.put(`/preziari/${selected.id}`, { nome: selected.nome, tipo: selected.tipo, markup: Number(markup) || 0 });
      toast.success(`Aumento del preziario impostato al ${Number(markup)}%`);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingMarkup(false); }
  };

  const saveMano = async () => {
    if (!selected) return;
    setSavingMano(true);
    try {
      await api.put(`/preziari/${selected.id}/manodopera`, { prezzo: Number(mano) || 0 });
      toast.success("Costo manodopera aggiornato");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingMano(false); }
  };

  const importFile = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Seleziona prima un file Excel o CSV"); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/preziari/${selected.id}/import?mode=${replaceMode ? "replace" : "append"}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Importate ${r.data.imported} voci (totale ${r.data.total})`);
      if (fileRef.current) fileRef.current.value = "";
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const importUrl = async () => {
    if (!url) { toast.error("Inserisci un link al file"); return; }
    setImporting(true);
    try {
      const r = await api.post(`/preziari/${selected.id}/import-url`, { url, mode: replaceMode ? "replace" : "append" });
      toast.success(`Importate ${r.data.imported} voci (totale ${r.data.total})`);
      setUrl("");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const deleteBook = async () => {
    try {
      await api.delete(`/preziari/${selected.id}`);
      toast.success("Preziario eliminato");
      setSelectedId(null);
      await loadBooks();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const filtered = items.filter((p) => !q || [p.codice, p.nome, p.categoria].join(" ").toLowerCase().includes(q.toLowerCase()));
  const shown = filtered.slice(0, 500);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground">Gestisci più preziari (regionali o personali). Le voci di tutti i preziari sono disponibili nei preventivi.</p>
        {isAdmin && (
          <Button onClick={() => { setNb({ nome: "", tipo: "personale", markup: 15 }); setNewOpen(true); }} data-testid="preziario-new-book-button">
            <Plus className="w-4 h-4 mr-1.5" /> Nuovo preziario
          </Button>
        )}
      </div>

      {/* Book selector */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="preziario-books">
        {books.map((b) => (
          <button
            key={b.id}
            onClick={() => setSelectedId(b.id)}
            className={`text-left rounded-xl border p-4 transition-all ${selectedId === b.id ? "border-primary ring-1 ring-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"}`}
            data-testid={`preziario-book-${b.id}`}
          >
            <div className="flex items-center gap-2">
              <BookMarked className="w-4 h-4 text-primary shrink-0" />
              <span className="font-heading font-semibold truncate">{b.nome}</span>
              <Badge variant="outline" className="ml-auto text-[10px] shrink-0">{b.tipo === "fvg" ? "Regionale" : "Personale"}</Badge>
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
              <span>{b.item_count} voci</span>
              <Badge variant="outline" className="text-primary border-primary/30">+{b.markup}%</Badge>
            </div>
          </button>
        ))}
        {books.length === 0 && <div className="text-sm text-muted-foreground col-span-full py-6 text-center">Nessun preziario. {isAdmin ? "Creane uno con 'Nuovo preziario'." : "Chiedi all'amministratore di crearne uno."}</div>}
      </div>

      {selected && (
        <>
          {isAdmin && (
            <div className="grid gap-6 lg:grid-cols-3">
              <div className="rounded-xl border border-border bg-card p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Percent className="w-5 h-5" /></div>
                  <div>
                    <h3 className="font-heading font-semibold">Aumento sui prezzi</h3>
                    <p className="text-xs text-muted-foreground">Applicato alle voci di «{selected.nome}»</p>
                  </div>
                </div>
                <div className="flex items-end gap-2">
                  <div className="flex-1 space-y-2">
                    <Label>Percentuale di aumento (%)</Label>
                    <Input type="number" step="0.5" value={markup} onChange={(e) => setMarkup(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveMarkup(); } }} data-testid="preziario-markup-input" />
                  </div>
                  <Button onClick={saveMarkup} disabled={savingMarkup} data-testid="preziario-markup-save">
                    {savingMarkup ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">Esempio: prezzo base 100 € → <span className="font-semibold text-foreground">{euro(100 * (1 + (Number(markup) || 0) / 100))}</span> nel preventivo.</p>
              </div>

              <div className="lg:col-span-2 rounded-xl border border-border bg-card p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Upload className="w-5 h-5" /></div>
                  <div>
                    <h3 className="font-heading font-semibold">Importa voci in «{selected.nome}»</h3>
                    <p className="text-xs text-muted-foreground">File Excel (.xlsx) o CSV con colonne: Codice, Descrizione, U.M., Prezzo</p>
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm cursor-pointer select-none w-fit">
                  <input type="checkbox" checked={replaceMode} onChange={(e) => setReplaceMode(e.target.checked)} className="accent-primary w-4 h-4" data-testid="preziario-replace-toggle" />
                  Sostituisci le voci esistenti di questo preziario
                </label>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="rounded-lg border border-dashed border-border p-4 space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium"><FileSpreadsheet className="w-4 h-4 text-primary" /> Da file</div>
                    <Input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv" data-testid="preziario-file-input" />
                    <Button className="w-full" onClick={importFile} disabled={importing} data-testid="preziario-import-file-button">
                      {importing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />} Importa file
                    </Button>
                  </div>
                  <div className="rounded-lg border border-dashed border-border p-4 space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium"><Link2 className="w-4 h-4 text-primary" /> Da link online</div>
                    <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…/preziario.xlsx" data-testid="preziario-url-input" />
                    <Button className="w-full" variant="outline" onClick={importUrl} disabled={importing} data-testid="preziario-import-url-button">
                      {importing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Link2 className="w-4 h-4 mr-1.5" />} Importa da link
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {isAdmin && (
            <div className="rounded-xl border border-border bg-card p-6 flex items-end gap-4 flex-wrap" data-testid="preziario-manodopera-card">
              <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><Hammer className="w-5 h-5" /></div>
              <div className="flex-1 min-w-[200px]">
                <h3 className="font-heading font-semibold">Manodopera</h3>
                <p className="text-xs text-muted-foreground">Costo orario di manodopera per «{selected.nome}». Diventa una voce selezionabile nei preventivi.</p>
              </div>
              <div className="space-y-2">
                <Label>Costo (€ / ora)</Label>
                <Input type="number" step="0.5" min="0" value={mano} onChange={(e) => setMano(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveMano(); } }} className="w-32" placeholder="Es. 35" data-testid="preziario-manodopera-input" />
              </div>
              <Button onClick={saveMano} disabled={savingMano || mano === ""} data-testid="preziario-manodopera-save">
                {savingMano ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Salva</>}
              </Button>
            </div>
          )}

          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="flex items-center justify-between gap-3 p-4 border-b border-border flex-wrap">
              <div className="flex items-center gap-2">
                <BookMarked className="w-4 h-4 text-primary" />
                <span className="font-heading font-semibold">{selected.nome}</span>
                <Badge variant="secondary">{selected.item_count} voci</Badge>
                <Badge variant="outline" className="text-primary border-primary/30">+{Number(markup)}%</Badge>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Cerca voce o codice…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="preziario-search-input" />
                </div>
                {isAdmin && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="icon" className="text-muted-foreground hover:text-destructive" data-testid="preziario-delete-book-button"><Trash2 className="w-4 h-4" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Eliminare il preziario «{selected.nome}»?</AlertDialogTitle>
                        <AlertDialogDescription>Verranno eliminate tutte le {selected.item_count} voci di questo preziario. Il tuo Listino Prezzi personale non viene toccato.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction onClick={deleteBook} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Elimina</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>
            <Table data-testid="preziario-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Cod.</TableHead>
                  <TableHead>Descrizione</TableHead>
                  <TableHead className="hidden sm:table-cell">U.M.</TableHead>
                  <TableHead className="text-right">Prezzo base</TableHead>
                  <TableHead className="text-right">Prezzo +{Number(markup)}%</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shown.map((p) => (
                  <TableRow key={p.id} data-testid={`preziario-row-${p.id}`}>
                    <TableCell className="font-mono text-xs">{p.codice || "—"}</TableCell>
                    <TableCell className="max-w-[520px]">{p.nome}</TableCell>
                    <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{p.unita || "—"}</TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">{euro(p.prezzo_base)}</TableCell>
                    <TableCell className="text-right font-semibold text-primary">{euro(p.prezzo_finale)}</TableCell>
                  </TableRow>
                ))}
                {selected.item_count === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-16 text-muted-foreground">
                    <BookMarked className="w-8 h-8 mx-auto mb-3 opacity-40" />
                    Nessuna voce in questo preziario. {isAdmin ? "Carica un file qui sopra." : ""}
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
            {filtered.length > 500 && <div className="p-3 text-center text-xs text-muted-foreground border-t border-border">Mostrate le prime 500 voci di {filtered.length}. Usa la ricerca per filtrare.</div>}
          </div>
        </>
      )}

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-md" data-testid="preziario-new-dialog" onKeyDown={(e) => { if (e.key === "Enter" && e.target.tagName === "INPUT") { e.preventDefault(); createBook(); } }}>
          <DialogHeader><DialogTitle className="font-heading">Nuovo preziario</DialogTitle><DialogDescription className="sr-only">Crea un nuovo listino preziario con nome, tipo e aumento percentuale.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome del preziario *</Label>
              <Input value={nb.nome} onChange={(e) => setNb((f) => ({ ...f, nome: e.target.value }))} placeholder="Es. Preziario Personale 2026" data-testid="preziario-new-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={nb.tipo} onValueChange={(t) => setNb((f) => ({ ...f, tipo: t }))}>
                  <SelectTrigger data-testid="preziario-new-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="personale">Personale</SelectItem>
                    <SelectItem value="fvg">Regionale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Aumento (%)</Label>
                <Input type="number" step="0.5" value={nb.markup} onChange={(e) => setNb((f) => ({ ...f, markup: e.target.value }))} data-testid="preziario-new-markup" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>Annulla</Button>
            <Button onClick={createBook} data-testid="preziario-new-save">Crea preziario</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
